/** The command-activity drawer: every command with its identity, status and expected versions (U043). */
import type { CommandRow } from "../contracts/commands.ts";
import { badge, instantCell } from "./components.ts";
import { h, setChildren } from "./dom.ts";

export interface CommandDrawer {
  readonly element: HTMLElement;
  setRows(rows: readonly CommandRow[]): void;
}

const UNRESOLVED = new Set(["APPLYING", "UNKNOWN"]);

function versions(row: CommandRow): string {
  return [...row.targetVersions].map(([name, version]) => `${name} v${String(version)}`).join(", ");
}

/** Build the drawer; it lists rows exactly as the commands projection and the stream deliver them. */
export function createCommandDrawer(): CommandDrawer {
  const list = h("ul", { class: "command-list" });
  const count = h("span", { class: "count" }, "0");
  const panel = h(
    "div",
    { attrs: { id: "command-panel", hidden: "" } },
    h(
      "p",
      { class: "caveat" },
      "A successful request or a delivered notification is not a fill. Unknown outcomes stay here until reconciled.",
    ),
    list,
  );
  const toggle = h(
    "button",
    {
      attrs: { type: "button", "aria-expanded": "false", "aria-controls": "command-panel" },
      on: {
        click: () => {
          const open = toggle.getAttribute("aria-expanded") !== "true";
          toggle.setAttribute("aria-expanded", String(open));
          panel.hidden = !open;
        },
      },
    },
    "Command activity (",
    count,
    ")",
  );
  const element = h(
    "aside",
    { class: "command-drawer", attrs: { "aria-label": "Command activity" } },
    toggle,
    panel,
  );
  return {
    element,
    setRows: (rows) => {
      count.textContent = String(rows.length);
      setChildren(
        list,
        rows.map((row) =>
          h(
            "li",
            { attrs: { "data-command": row.commandId, "data-state": row.state } },
            badge(
              row.state,
              row.state === "APPLIED"
                ? "ok"
                : UNRESOLVED.has(row.state) || row.state === "FAILED" || row.state === "REJECTED"
                  ? "bad"
                  : "info",
            ),
            ` ${row.action} by ${row.actorIdentityId} on ${row.accountId}; command ${row.commandId.slice(0, 8)}; expects ${versions(row)}; received `,
            instantCell(row.receivedAt),
            UNRESOLVED.has(row.state)
              ? h("strong", {}, " Outcome unresolved: inspect, do not assume it is closed.")
              : null,
          ),
        ),
      );
    },
  };
}
